import { useEffect, useRef, type ReactNode } from 'react';
import { Warning } from '@phosphor-icons/react';

interface Props {
  // Localized heading shown next to the warning icon. Should describe
  // what the user just tried that failed ("Couldn't save the rules"),
  // not the abstract concept of "Error" — the icon and color already
  // carry that signal.
  heading: string;
  // Single message (basic modal flow) OR a bulleted list of specific
  // issues (rules modal preflight). Pass whichever variant the
  // surrounding form produces — both render with the same chrome.
  children: ReactNode;
}

// FormErrorCallout — red-bordered alert that auto-scrolls itself into
// view when it mounts. Used at the top of long forms (category rules,
// basic category save) where the bottom-of-form `<p>` was easy to miss
// once the user had scrolled mid-form to fix something else.
// The scroll runs on mount; if the message changes later, parents can
// remount the callout by switching the React `key` to refresh.
export function FormErrorCallout({ heading, children }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Scroll only the nearest actually-scrollable ancestor. We avoid
    // Element.scrollIntoView() because it walks up every scrollable
    // ancestor (per spec, `overflow: hidden` counts) and, when this
    // callout is inside a <dialog> with `overflow-hidden`, has been
    // observed to leave the dialog itself scrolled to an offset that
    // clips the sticky header / footer — so the close button becomes
    // unreachable.
    let parent: HTMLElement | null = el.parentElement;
    while (parent) {
      const overflowY = getComputedStyle(parent).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') {
        const target =
          el.getBoundingClientRect().top -
          parent.getBoundingClientRect().top +
          parent.scrollTop;
        parent.scrollTo({ top: target, behavior: 'smooth' });
        return;
      }
      parent = parent.parentElement;
    }
  }, []);
  return (
    <div
      ref={ref}
      role="alert"
      aria-live="polite"
      className="rounded-xl border border-destructive bg-destructive/10 p-4"
    >
      <div className="flex items-start gap-3">
        <Warning
          weight="fill"
          className="mt-0.5 h-5 w-5 shrink-0 text-destructive"
          aria-hidden
        />
        <div className="flex min-w-0 flex-1 flex-col gap-2 text-sm text-foreground">
          <p className="font-heading font-semibold text-destructive">
            {heading}
          </p>
          {children}
        </div>
      </div>
    </div>
  );
}
